import { wrapPromiseWithFetchInteraction } from '@jira/polaris__lib-analytics/src/wrapPromiseWithFetchInteraction.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { Permission, PermissionsCheckResponse } from './PermissionsResponse.types.tsx';
import { getEarlyFetchResponseForProjects } from './runGetPermissionsEarlyScript.tsx';
import { buildPermissionsCheckRequestBody, PERMISSIONS_CHECK_PATH } from './getPermissionsUrl.tsx';
import { GLOBAL_PERMISSIONS, PROJECT_PERMISSIONS } from './SupportedPermissions.tsx';
import { comparePermissionsWithLegacy } from './comparePermissionsWithLegacy.tsx';

const mapPermissionsCheckResponse = (
	response: PermissionsCheckResponse,
	projectIds: number[],
): Record<string, Permission[]> => {
	const projectsByPermission = new Map<string, Set<number>>();
	for (const entry of response.projectPermissions) {
		projectsByPermission.set(entry.permission, new Set(entry.projects));
	}
	const grantedGlobalPermissions = new Set(response.globalPermissions);

	const permissionsByProjectId: Record<string, Permission[]> = {};

	for (const projectId of projectIds) {
		const permissions: Permission[] = [];

		for (const key of PROJECT_PERMISSIONS) {
			const grantedProjectIds = projectsByPermission.get(key);
			permissions.push({
				key,
				havePermission: grantedProjectIds?.has(projectId) ?? false,
			});
		}

		for (const key of GLOBAL_PERMISSIONS) {
			permissions.push({
				key,
				havePermission: grantedGlobalPermissions.has(key),
			});
		}

		permissionsByProjectId[String(projectId)] = permissions;
	}

	return permissionsByProjectId;
};

/** Early preload via the bulk permissions/check POST endpoint. */
const tryGetFromEarlyFetchForProjects = async (
	projectIds: number[],
): Promise<Record<string, Permission[]> | null> => {
	const cachedPromise = getEarlyFetchResponseForProjects(projectIds);
	if (!cachedPromise) {
		return null;
	}

	try {
		const response = await wrapPromiseWithFetchInteraction(
			cachedPromise,
			'earlyScript/getPermissionsForProjects',
		);
		const mappedResult = mapPermissionsCheckResponse(response, projectIds);
		comparePermissionsWithLegacy(projectIds, mappedResult).catch(() => undefined);
		return mappedResult;
	} catch {
		return null;
	}
};

const fetchPermissionsCheck = (projectIds: number[]): Promise<Record<string, Permission[]>> =>
	fetchJson<PermissionsCheckResponse>(PERMISSIONS_CHECK_PATH, {
		method: 'POST',
		body: JSON.stringify(buildPermissionsCheckRequestBody(projectIds)),
	}).then((response) => {
		const mappedResult = mapPermissionsCheckResponse(response, projectIds);
		comparePermissionsWithLegacy(projectIds, mappedResult).catch(() => undefined);
		return mappedResult;
	});

export const fetchPermissionsForProjects = async (
	projectIds: number[],
): Promise<Record<string, Permission[]>> => {
	const fromEarlyFetchForProjects = await tryGetFromEarlyFetchForProjects(projectIds);
	if (fromEarlyFetchForProjects) {
		return fromEarlyFetchForProjects;
	}

	return fetchPermissionsCheck(projectIds);
};
